/**
 * Card hover carousel
 *
 * On hovering a product card, lazy-fetches the product's full image gallery
 * and overlays a horizontal sliding carousel inside the card's media area.
 * Slides advance every ~1s while the cursor is over the card, animating
 * smoothly via CSS transform: translateX(). On mouseleave the overlay is
 * removed and the original card image returns.
 *
 * Pure progressive enhancement — no theme markup changes. The original
 * <img> inside .card__media stays in place; the carousel overlay sits on
 * top of it absolutely, so we never disturb the theme's srcset or the
 * card's layout.
 *
 * Skipped on touch devices (no meaningful hover) and when
 * prefers-reduced-motion is set.
 */

(function () {
  const CACHE = new Map();
  const HOVER_DELAY = 180;     // ms — pause so quick mouse pass-through doesn't trigger
  const ADVANCE_MS = 1000;     // ms between slides
  const SLIDE_TRANSITION_MS = 700; // ms for the CSS transform transition
  const IMAGE_WIDTH = 533;     // matches the card's image_url width
  const MAX_IMAGES = 8;

  const isTouch =
    'ontouchstart' in window ||
    (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0);

  const prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Dots can render on touch (purely visual hint that more images exist).
  // The hover carousel itself is desktop-only and respects reduced-motion.
  const canRunCarousel = !isTouch && !prefersReducedMotion;

  /* -------- Helpers -------- */

  function extractHandle(href) {
    if (!href) return null;
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function sized(url, w) {
    if (!url) return '';
    if (/_\d+x\d*\./.test(url)) return url;
    return url.replace(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i, `_${w}x.$1$2`);
  }

  function preload(urls) {
    urls.forEach((u) => {
      if (!u) return;
      const i = new Image();
      i.src = u;
    });
  }

  async function fetchImages(handle) {
    if (CACHE.has(handle)) return CACHE.get(handle);
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) {
        CACHE.set(handle, []);
        return [];
      }
      const product = await res.json();
      const images = Array.isArray(product.images) ? product.images : [];
      const sizedUrls = images.slice(0, MAX_IMAGES).map((u) => sized(u, IMAGE_WIDTH));
      CACHE.set(handle, sizedUrls);
      return sizedUrls;
    } catch (e) {
      CACHE.set(handle, []);
      return [];
    }
  }

  /* -------- State (per card) -------- */

  const state = new WeakMap();

  function getState(card) {
    let s = state.get(card);
    if (!s) {
      s = {
        bound: false,
        dotsRendered: false,
        hoverTimer: null,
        interval: null,
        track: null,
        container: null,
        dotEls: null,
        slideCount: 0,
      };
      state.set(card, s);
    }
    return s;
  }

  /* -------- Dot indicators -------- */

  // Find the outer media container for absolute-positioned overlays. Prefer
  // .card__media (always present, full image area) over .media (inner
  // wrapper that's often aspect-ratio padding-hack and can be measured as 0).
  function findMediaContainer(card) {
    return (
      card.querySelector('.card__media') ||
      card.querySelector('.card__inner') ||
      card.querySelector('.card-wrapper > div') ||
      card
    );
  }

  function renderDots(card, count) {
    const s = getState(card);
    if (s.dotsRendered) return;

    const container = findMediaContainer(card);
    if (!container) return;

    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }

    const wrap = document.createElement('div');
    wrap.className = 'card-hover-dots';
    wrap.setAttribute('aria-hidden', 'true');

    const dots = [];
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.className = 'card-hover-dots__dot';
      if (i === 0) dot.classList.add('card-hover-dots__dot--active');
      wrap.appendChild(dot);
      dots.push(dot);
    }
    container.appendChild(wrap);

    s.dotsRendered = true;
    s.dotEls = dots;
    s.slideCount = count;
  }

  function setActiveDot(card, index) {
    const s = state.get(card);
    if (!s || !s.dotEls) return;
    s.dotEls.forEach((d, i) => {
      d.classList.toggle('card-hover-dots__dot--active', i === index);
    });
  }

  /* -------- Viewport-driven count fetcher (renders dots) -------- */

  const viewportObserver =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              const card = entry.target;
              viewportObserver.unobserve(card);

              const link = card.querySelector('a[href*="/products/"]');
              const handle = extractHandle(link && link.getAttribute('href'));
              if (!handle) continue;

              fetchImages(handle).then((images) => {
                if (images.length > 1) renderDots(card, images.length);
              });
            }
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  function observeCardsForDots() {
    if (!viewportObserver) return;
    document.querySelectorAll('.card-wrapper').forEach((card) => {
      if (card.dataset.cardDotsObserved === '1') return;
      card.dataset.cardDotsObserved = '1';
      viewportObserver.observe(card);
    });
  }

  // Run on initial DOM ready, and re-run any time new cards are added by
  // facet filters / pagination AJAX swaps.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCardsForDots);
  } else {
    observeCardsForDots();
  }

  // Re-observe after AJAX changes to the product grid. MutationObserver on
  // body is broad but cheap (filter is rare and we just call querySelectorAll).
  const mutationObserver = new MutationObserver(() => observeCardsForDots());
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  /* -------- Build / teardown -------- */

  async function start(card, handle) {
    const s = getState(card);
    if (s.track || s.interval) return; // already running

    const images = await fetchImages(handle);
    if (images.length < 2) return;
    if (!card.matches(':hover')) return; // bailed during fetch

    const container = findMediaContainer(card);
    if (!container) return;

    if (getComputedStyle(container).position === 'static') {
      container.style.position = 'relative';
    }

    // Slide 0 is a TRANSPARENT placeholder — no IMG element. The underlying
    // card image shows through, so when the overlay is inserted there is
    // literally nothing to paint differently → zero flicker. Slides 1+ are
    // real images that slide in over the card image as the carousel advances,
    // and when we loop back to slide 0 they slide off again to reveal the
    // original card image underneath.
    const realImageUrls = images.slice(); // images[0] is the featured image

    // Build the carousel: outer (overflow hidden) > track (transforms) > slides
    const outer = document.createElement('div');
    outer.className = 'card-hover-carousel';
    outer.setAttribute('aria-hidden', 'true');

    const track = document.createElement('div');
    track.className = 'card-hover-carousel__track';

    // Slide 0: transparent placeholder
    const placeholderSlide = document.createElement('div');
    placeholderSlide.className =
      'card-hover-carousel__slide card-hover-carousel__slide--placeholder';
    track.appendChild(placeholderSlide);

    // Slides 1+ from the product's gallery (images[1] onward — we skip the
    // featured image since that's what's already showing on the card)
    const visibleImages = realImageUrls.slice(1);
    for (const url of visibleImages) {
      const slide = document.createElement('div');
      slide.className = 'card-hover-carousel__slide';
      const slideImg = document.createElement('img');
      slideImg.className = 'card-hover-carousel__image';
      slideImg.src = url;
      slideImg.alt = '';
      slideImg.loading = 'eager';
      slide.appendChild(slideImg);
      track.appendChild(slide);
    }

    outer.appendChild(track);
    container.appendChild(outer);
    s.container = container;
    s.track = outer;

    // Preload remaining images so each slide transition is instant
    preload(visibleImages);

    outer.classList.add('card-hover-carousel--visible');

    const slideCount = visibleImages.length + 1; // includes placeholder
    let index = 0;
    s.interval = window.setInterval(() => {
      index = (index + 1) % slideCount;
      track.style.transform = `translateX(-${index * 100}%)`;
      // Active dot indexes against the FULL image set (placeholder = featured image)
      setActiveDot(card, index);
    }, ADVANCE_MS);
  }

  function stop(card) {
    const s = state.get(card);
    if (!s) return;

    if (s.hoverTimer) {
      clearTimeout(s.hoverTimer);
      s.hoverTimer = null;
    }
    if (s.interval) {
      clearInterval(s.interval);
      s.interval = null;
    }
    if (s.track) {
      // Remove immediately — no fade-out. The user has moved their cursor
      // off the card already, so a delayed transition would feel like lag.
      if (s.track.parentNode) s.track.parentNode.removeChild(s.track);
      s.track = null;
      s.container = null;
    }
    // Reset the dot indicator so the next hover starts fresh on dot 0
    setActiveDot(card, 0);
  }

  /* -------- Event delegation -------- */

  // Hover-driven carousel — desktop only. Dots above still render on touch.
  if (!canRunCarousel) return;

  // mouseover bubbles, so a single document listener catches dynamically
  // inserted cards (AJAX filter swaps). On first mouseover for a card we
  // attach proper mouseenter/mouseleave listeners — these fire on EVERY
  // enter/leave thereafter, so the carousel re-triggers on subsequent
  // hovers (previously a `bound: true` short-circuit blocked re-triggers).
  document.addEventListener('mouseover', (event) => {
    const card = event.target.closest('.card-wrapper');
    if (!card) return;

    const s = getState(card);
    if (s.bound) return;

    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    const handle = extractHandle(link.getAttribute('href'));
    if (!handle) return;

    s.bound = true;
    card.dataset.cardHoverCarousel = handle;

    // Per-card listeners — fire on every enter/leave
    card.addEventListener('mouseenter', () => {
      // Don't double-schedule if a previous start is still in progress
      if (s.hoverTimer || s.interval || s.track) return;
      s.hoverTimer = window.setTimeout(() => {
        s.hoverTimer = null;
        start(card, handle);
      }, HOVER_DELAY);
    });
    card.addEventListener('mouseleave', () => stop(card));

    // The cursor is already over the card right now (that's why the mouseover
    // delegated handler fired). Schedule a start for this current hover too,
    // since mouseenter won't re-fire until the next entry.
    s.hoverTimer = window.setTimeout(() => {
      s.hoverTimer = null;
      start(card, handle);
    }, HOVER_DELAY);
  });
})();
